import { Badge } from "@hortis/ui/badge";
import { IconButton } from "@hortis/ui/button";
import { DialogTrigger } from "@hortis/ui/dialog";
import { Edit2, Plus } from "@hortis/ui/icons";
import { Tooltip, TooltipContent, TooltipTrigger } from "@hortis/ui/tooltip";
import { format } from "date-fns";
import { AccessionFieldsFragment } from "generated/graphql";
import {
  RecordSection,
  RecordSectionDataGroup,
  RecordSectionDataItem,
  RecordSectionDataItemLabel,
  RecordSectionDataItemValue,
  RecordSectionTitle,
} from "src/components/record-section";
import { stopEventPropagation } from "src/features/collection/components/stop-event-propagation";
import {
  IdentificationDialog,
  extractIdentificationFields,
} from "src/features/create-accession/components/accession-data-form/identification-dialog";
import { parseUtcDate } from "src/utils/dates/parse-utc-date";
import {
  identificationQualifierLabelMap,
  identificationVerificationStatusMap,
} from "src/utils/label-maps/identification";
import { twMerge } from "tailwind-merge";

const getHasData = (accession: AccessionFieldsFragment) => {
  return (
    accession.identifiedBy != null ||
    accession.identificationRemarks != null ||
    accession.identificationQualifier != null ||
    accession.identificationVerificationStatus != null ||
    accession.dateIdentified != null
  );
};

export const AccessionIdentificationSection = ({
  accession,
  canEdit,
}: { accession: AccessionFieldsFragment; canEdit: boolean }) => {
  const hasData = getHasData(accession);

  return hasData || canEdit ? (
    <RecordSection
      className={twMerge(!hasData && "border-dashed shadow-none pb-3")}
      data-cy="identification-section"
    >
      <RecordSectionTitle>
        <div className="flex items-center gap-3">
          Identification
          {accession.dateIdentified != null && (
            <Badge
              color="grey"
              type="icon"
              className="border border-grey-200"
              testId="identification-date"
            >
              {format(parseUtcDate(accession.dateIdentified), "PPP")}
            </Badge>
          )}
        </div>
        {canEdit && (
          <IdentificationDialog
            accessionId={accession.id}
            initialData={
              hasData ? extractIdentificationFields(accession) : undefined
            }
          >
            <Tooltip>
              <TooltipTrigger asChild>
                <DialogTrigger asChild onFocusCapture={stopEventPropagation}>
                  <IconButton
                    ariaLabel={
                      hasData ? "Edit identification" : "Add identification"
                    }
                    icon={hasData ? Edit2 : Plus}
                    size="sm"
                    variant="tertiaryGray"
                    testId="identification-edit-button"
                  />
                </DialogTrigger>
              </TooltipTrigger>
              <TooltipContent side="left">
                {hasData
                  ? "Change identification details"
                  : "Add identification details"}
              </TooltipContent>
            </Tooltip>
          </IdentificationDialog>
        )}
      </RecordSectionTitle>
      {hasData && (
        <>
          <RecordSectionDataGroup>
            {accession.identificationVerificationStatus != null && (
              <RecordSectionDataItem data-cy="identification-verification">
                <RecordSectionDataItemLabel>
                  Verification
                </RecordSectionDataItemLabel>
                <RecordSectionDataItemValue>
                  {
                    identificationVerificationStatusMap[
                      accession.identificationVerificationStatus
                    ]
                  }{" "}
                  ({accession.identificationVerificationStatus})
                </RecordSectionDataItemValue>
              </RecordSectionDataItem>
            )}
            {accession.identifiedBy != null && (
              <RecordSectionDataItem data-cy="identification-by">
                <RecordSectionDataItemLabel>
                  Identified by
                </RecordSectionDataItemLabel>
                <RecordSectionDataItemValue>
                  {accession.identifiedBy}
                </RecordSectionDataItemValue>
              </RecordSectionDataItem>
            )}
            {accession.identificationQualifier != null && (
              <RecordSectionDataItem data-cy="identification-qualifier">
                <RecordSectionDataItemLabel>
                  Identified qualifier
                </RecordSectionDataItemLabel>
                <RecordSectionDataItemValue>
                  {
                    identificationQualifierLabelMap[
                      accession.identificationQualifier
                    ]
                  }{" "}
                  ({accession.identificationQualifier})
                </RecordSectionDataItemValue>
              </RecordSectionDataItem>
            )}
            {accession.identificationRemarks != null && (
              <RecordSectionDataItem data-cy="identification-remarks">
                <RecordSectionDataItemLabel>Remarks</RecordSectionDataItemLabel>
                <RecordSectionDataItemValue>
                  {accession.identificationRemarks}
                </RecordSectionDataItemValue>
              </RecordSectionDataItem>
            )}
          </RecordSectionDataGroup>
        </>
      )}
    </RecordSection>
  ) : null;
};
